import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FileSignature, CheckCircle, Clock, Send, Eye } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';

const statusColor = (s) => {
  if (s === 'completed') return 'bg-emerald-100 text-emerald-700';
  if (s === 'sent') return 'bg-blue-100 text-blue-700';
  if (s === 'rejected') return 'bg-red-100 text-red-700';
  return 'bg-amber-100 text-amber-700';
};

export default function CustomerContracts() {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(null);
  const [viewDialog, setViewDialog] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);

  const fetchData = () => {
    api.get('/customer/contracts')
      .then(r => setContracts(r.data.contracts || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, []);

  const handleSign = async (contractId) => {
    setSigning(contractId);
    try {
      await api.put(`/customer/contracts/${contractId}/sign`);
      toast.success('Kontrak berhasil ditandatangani');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal menandatangani kontrak');
    } finally {
      setSigning(null);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--store-primary, #2D6CDF)]" />
    </div>
  );

  return (
    <div data-testid="customer-contracts-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>Kontrak Digital</h1>
        <p className="text-sm text-slate-500 mt-1">Lihat dan tandatangani kontrak proyek Anda</p>
      </div>

      {contracts.length === 0 ? (
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="py-16 text-center">
            <FileSignature size={48} className="mx-auto text-slate-300 mb-4" />
            <h3 className="font-medium text-slate-900 mb-1">Belum ada kontrak</h3>
            <p className="text-sm text-slate-500">Kontrak dari pembelian high-ticket akan muncul di sini</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {contracts.map(contract => (
            <Card key={contract.id} className="border-slate-200 shadow-sm">
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center shrink-0">
                      <FileSignature size={20} className="text-indigo-500" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">{contract.title}</h3>
                      <p className="text-sm text-slate-500 mt-0.5">{contract.description || 'Kontrak proyek'}</p>
                      <p className="text-xs text-slate-400 mt-1">
                        {new Date(contract.created_at).toLocaleDateString('id-ID', { day: '2-digit', month: 'long', year: 'numeric' })}
                      </p>
                      <div className="flex items-center gap-3 mt-2">
                        <span className={`flex items-center gap-1 text-xs ${contract.customer_signed ? 'text-emerald-600' : 'text-slate-400'}`}>
                          <CheckCircle size={12} /> Anda {contract.customer_signed ? 'sudah' : 'belum'} tanda tangan
                        </span>
                        <span className={`flex items-center gap-1 text-xs ${contract.tenant_signed ? 'text-emerald-600' : 'text-slate-400'}`}>
                          <CheckCircle size={12} /> Pihak toko {contract.tenant_signed ? 'sudah' : 'belum'} tanda tangan
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <Badge className={statusColor(contract.status)}>{contract.status}</Badge>
                    <div className="flex gap-2 mt-1">
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-xs h-7 px-2"
                        onClick={() => { setSelectedContract(contract); setViewDialog(true); }}
                      >
                        <Eye size={12} className="mr-1" /> Lihat
                      </Button>
                      {contract.status === 'sent' && !contract.customer_signed && (
                        <Button
                          size="sm"
                          className="text-xs h-7 px-2 bg-emerald-600 hover:bg-emerald-700"
                          onClick={() => handleSign(contract.id)}
                          disabled={signing === contract.id}
                        >
                          <FileSignature size={12} className="mr-1" />
                          {signing === contract.id ? 'Menandatangani...' : 'Tanda Tangan'}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* View Contract Dialog */}
      <Dialog open={viewDialog} onOpenChange={setViewDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedContract?.title}</DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <div className="bg-slate-50 rounded-lg p-6 text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
              {selectedContract?.content || 'Konten kontrak tidak tersedia'}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewDialog(false)}>Tutup</Button>
            {selectedContract?.status === 'sent' && !selectedContract?.customer_signed && (
              <Button
                className="bg-emerald-600 hover:bg-emerald-700"
                onClick={() => { handleSign(selectedContract.id); setViewDialog(false); }}
                disabled={signing === selectedContract?.id}
              >
                <FileSignature size={16} className="mr-2" /> Tandatangani Kontrak
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
